<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ListaItens, type SeletorItem } from '@Lar21/imaginii-web-components/vue'
import { encontrarOpcaoDoCatalogo, preservarEstadoSemSelecaoVazia, prepararEstadoSomenteLeitura } from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'
import { CHAVE_I18N_CATALOGO_MOTIVO_PADRAO, criarEstadoSeletorCatalogoMotivoPadrao, criarPropriedadesPontotelSeletorMotivoPadrao, extrairValorDoSeletorCatalogoMotivoPadrao, filtrarOpcoesMotivoPadrao } from './adaptador-catalogo-motivo-padrao'
import { usarCatalogoMotivoPadrao } from './usar-catalogo-motivo-padrao'
import type { FiltrosCatalogoMotivoPadrao, OpcaoMotivoPadrao, SelecaoCatalogoMotivoPadrao } from '../catalogo-motivo-padrao.tipos'
type Props = Readonly<{
  modelValue: SelecaoCatalogoMotivoPadrao
  selecaoMultipla?: boolean
  filtros?: FiltrosCatalogoMotivoPadrao
  preservarSelecaoForaDoCatalogo?: boolean
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
}>
const props = withDefaults(defineProps<Props>(), { selecaoMultipla: false, preservarSelecaoForaDoCatalogo: false, desabilitado: false, somenteLeitura: false, permiteSelecionarTodos: true, permiteLimpar: true, habilitarDuploClique: false, mensagemErro: null })
const emit = defineEmits<{
  'update:modelValue': [valor: SelecaoCatalogoMotivoPadrao]
  'filtrando-itens': [filtro: string]
  'duplo-clique': [opcao: OpcaoMotivoPadrao]
  'update:selecionouTodos': [selecionouTodos: boolean]
}>()
const { t } = useI18n()
const catalogo = usarCatalogoMotivoPadrao(computed(() => !props.somenteLeitura))
const opcoes = computed(() => {
  const opcoesFiltradas = filtrarOpcoesMotivoPadrao(catalogo.itens.value, props.filtros)
  const selecao: readonly OpcaoMotivoPadrao[] = props.modelValue === null
    ? []
    : (Array.isArray(props.modelValue)
        ? props.modelValue as readonly OpcaoMotivoPadrao[]
        : [props.modelValue as OpcaoMotivoPadrao])
  const idsSelecionados = new Set(selecao.map(({ id }) => id))
  const idsFiltrados = new Set(opcoesFiltradas.map(({ id }) => id))
  const opcoesSelecionadasForaDoFiltro = props.preservarSelecaoForaDoCatalogo
    ? catalogo.itens.value.filter(({ id }) => (
      idsSelecionados.has(id) && !idsFiltrados.has(id)
    ))
    : []
  const idsDisponiveis = new Set([
    ...idsFiltrados,
    ...opcoesSelecionadasForaDoFiltro.map(({ id }) => id),
  ])
  const opcoesSelecionadasForaDoCatalogo = props.preservarSelecaoForaDoCatalogo
    ? selecao.flatMap((opcao) => {
      if (idsDisponiveis.has(opcao.id)) return []
      return typeof opcao.description === 'string' && opcao.description.trim() ? [opcao] : []
    })
    : []

  return [
    ...opcoesFiltradas,
    ...opcoesSelecionadasForaDoFiltro,
    ...opcoesSelecionadasForaDoCatalogo,
  ]
})
const estado = computed(() => prepararEstadoSomenteLeitura(
  criarEstadoSeletorCatalogoMotivoPadrao(opcoes.value, props.modelValue, props.selecaoMultipla),
  props.somenteLeitura,
  props.modelValue,
))
const erro = computed(() => catalogo.erro.value ? t(`${CHAVE_I18N_CATALOGO_MOTIVO_PADRAO}.erroBusca`) : props.mensagemErro)
const propriedades = computed(() => criarPropriedadesPontotelSeletorMotivoPadrao({
  traduzir: t,
  ...(props.label !== undefined ? { label: props.label } : {}),
  ...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {}),
  erro: erro.value,
}))
const alterarSelecao = (novoEstado: ListaItens): void => { emit('update:modelValue', extrairValorDoSeletorCatalogoMotivoPadrao(
  preservarEstadoSemSelecaoVazia(novoEstado, estado.value, props.permiteLimpar),
  opcoes.value,
  props.selecaoMultipla,
)); }
const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoes.value, item)
  if (opcao) emit('duplo-clique', opcao)
}
</script>
<template>
  <PontotelSeletor
    v-bind="propriedades"
    :model-value="estado"
    :carregar-itens="null"
    :selecao-multipla="selecaoMultipla"
    :desabilitado="desabilitado"
    :modo-exibicao="somenteLeitura"
    :permite-selecionar-todos="permiteSelecionarTodos"
    :habilitar-duplo-clique="habilitarDuploClique"
    :recarregar-todos="!somenteLeitura"
    :recarregando-todos="catalogo.atualizando.value"
    @update:model-value="alterarSelecao"
    @filtrando-itens="emit('filtrando-itens', $event)"
    @duplo-clique="tratarDuploClique"
    @update:selecionou-todos="emit('update:selecionouTodos', $event)"
    @recarregar-todos="catalogo.atualizar"
  />
</template>
